import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BookOpen, ChevronDown, Monitor, Users, User, Lightbulb, PlayCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Section registry ─── */
const SECTION_IDS = [
  'getting-started', 'two-engines', 'finding-content', 'running-activity', 'game-engines',
  'saving-setup', 'teaching-sequences', 'tracking-progress', 'groups',
];
const SECTION_LABEL_KEYS = {
  'getting-started': 'guide.sec.gettingStarted',
  'two-engines': 'guide.sec.twoEngines',
  'finding-content': 'guide.sec.findingContent',
  'running-activity': 'guide.sec.runningActivity',
  'game-engines': 'guide.sec.gameEngines',
  'saving-setup': 'guide.sec.savingSetup',
  'teaching-sequences': 'guide.sec.teachingSequences',
  'tracking-progress': 'guide.sec.trackingProgress',
  'groups': 'guide.sec.groups',
};

/* ─── Game engine data ─── */
const ENGINES = [
  { id: 'la-course', name: 'La Course', tags: ['Teams', 'Head-to-Head'] },
  { id: 'le-duel', name: 'Le Duel', tags: ['Teams', 'Head-to-Head'] },
  { id: 'la-tour', name: 'La Tour', tags: ['Solo', 'Whole Class'] },
  { id: 'lavalanche', name: "L'Avalanche", tags: ['Solo', 'Whole Class'] },
  { id: 'tri-express', name: 'Tri Express', tags: ['Whole Class'] },
  { id: 'batisseur', name: 'B\u00e2tisseur', tags: ['Whole Class'] },
  { id: 'la-memoire', name: 'La M\u00e9moire', tags: ['Whole Class'] },
  { id: 'robot-balayeur', name: 'Robot Balayeur', tags: ['Whole Class'] },
  { id: 'horloge-folle', name: "L'Horloge Folle", tags: ['Solo', 'Whole Class'] },
  { id: 'le-detective', name: 'Le D\u00e9tective', tags: ['Whole Class'] },
  { id: 'carte-du-canada', name: 'Carte du Canada', tags: ['Whole Class'] },
  { id: 'le-chef', name: 'Le Chef', tags: ['Whole Class'] },
];

/* ─── Helper components ─── */

function ScreenFrame({ src, alt, caption }) {
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState(false);
  if (err) return null;
  return (
    <div data-testid={`screen-frame-${alt}`} style={{
      margin: '1.75rem 0', borderRadius: '10px', overflow: 'hidden',
      boxShadow: '0 2px 20px rgba(0,0,0,0.06)', border: '1px solid #E2E8F0',
    }}>
      <div style={{
        background: '#F1F3F5', padding: '10px 14px',
        display: 'flex', gap: '7px', alignItems: 'center',
      }}>
        <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FF5F57' }} />
        <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FEBC2E' }} />
        <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#28C840' }} />
      </div>
      <img
        src={src} alt={alt}
        onLoad={() => setOk(true)}
        onError={() => setErr(true)}
        style={{ width: '100%', display: 'block', opacity: ok ? 1 : 0, transition: 'opacity 0.3s' }}
      />
      {caption && ok && (
        <div style={{
          padding: '0.625rem 1rem', background: '#FAFBFC', borderTop: '1px solid #F1F5F9',
          fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#64748B', fontStyle: 'italic',
        }}>
          {caption}
        </div>
      )}
    </div>
  );
}

function Tip({ children }) {
  return (
    <div style={{
      margin: '1rem 0', padding: '0.75rem 1rem', paddingLeft: '1rem',
      background: '#F8FAFC', borderLeft: '3px solid #94A3B8',
      borderRadius: '0 6px 6px 0',
    }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
        fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', color: '#475569', lineHeight: 1.65,
      }}>
        <Lightbulb size={15} style={{ flexShrink: 0, marginTop: '3px', color: '#94A3B8' }} />
        <span>{children}</span>
      </div>
    </div>
  );
}

function SectionHeading({ id, children }) {
  return (
    <h2
      id={id}
      data-testid={`guide-section-${id}`}
      style={{
        fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 700,
        color: '#1A1D20', margin: 0, paddingTop: '2.5rem', paddingBottom: '0.75rem',
        scrollMarginTop: '1rem',
      }}
    >
      {children}
    </h2>
  );
}

function SubHeading({ children }) {
  return (
    <h3 style={{
      fontFamily: 'Outfit, sans-serif', fontSize: '1.1rem', fontWeight: 700,
      color: '#1A1D20', margin: '1.75rem 0 0.5rem',
    }}>
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p style={{
      fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem',
      color: '#374151', lineHeight: 1.75, margin: '0 0 1rem',
    }}>
      {children}
    </p>
  );
}

function Strong({ children }) {
  return <strong style={{ fontWeight: 700, color: '#1A1D20' }}>{children}</strong>;
}

function EngineCard({ engine, expanded, onToggle, t }) {
  return (
    <div
      data-testid={`engine-card-${engine.id}`}
      style={{
        border: '1px solid #E2E8F0', borderRadius: '0.625rem',
        background: 'white', overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        boxShadow: expanded ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
      }}
    >
      {/* Header — always visible */}
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          width: '100%', padding: '0.875rem 1rem', background: 'transparent',
          border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <ChevronDown
          size={16}
          color="#94A3B8"
          style={{ flexShrink: 0, transition: 'transform 0.2s', transform: expanded ? 'rotate(0)' : 'rotate(-90deg)' }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#1A1D20' }}>
              {engine.name}
            </span>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#94A3B8' }}>
              {t(`engine.${engine.id}.subtitle`)}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.375rem', flexShrink: 0 }}>
          {engine.tags.map(tag => (
            <span key={tag} style={{
              fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', fontWeight: 600,
              padding: '0.2rem 0.5rem', borderRadius: '0.25rem',
              background: tag === 'Teams' || tag === 'Head-to-Head' ? '#FEF2F2' : '#F0F4FF',
              color: tag === 'Teams' || tag === 'Head-to-Head' ? '#B91C1C' : '#1E40AF',
            }}>
              {tag === 'Teams' && <Users size={10} style={{ marginRight: '3px', verticalAlign: 'middle' }} />}
              {tag === 'Solo' && <User size={10} style={{ marginRight: '3px', verticalAlign: 'middle' }} />}
              {tag === 'Whole Class' && <Monitor size={10} style={{ marginRight: '3px', verticalAlign: 'middle' }} />}
              {t(`tag.${tag}`)}
            </span>
          ))}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '0 1rem 1rem 2.75rem', borderTop: '1px solid #F1F5F9' }}>
          <p style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', color: '#374151',
            lineHeight: 1.7, margin: '0.875rem 0 0',
          }}>
            {t(`engine.${engine.id}.desc`)}
          </p>
          <Tip>{t(`engine.${engine.id}.tip`)}</Tip>
        </div>
      )}
    </div>
  );
}


/* ─── Main component ─── */

export function TeacherGuide() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [expandedEngines, setExpandedEngines] = useState(new Set());
  const [walkthrough, setWalkthrough] = useState(null);
  const contentRef = useRef(null);
  const { t } = useLanguage();

  /* Fetch the owner-controlled walkthrough video (public endpoint). */
  useEffect(() => {
    axios.get(`${API_URL}/walkthrough-video`)
      .then(res => setWalkthrough(res.data?.video || null))
      .catch(() => setWalkthrough(null));
  }, []);

  const sectionIds = walkthrough?.embed_url
    ? ['walkthrough', ...SECTION_IDS]
    : SECTION_IDS;

  /* Track active section via IntersectionObserver */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: '-10% 0px -80% 0px', root: contentRef.current }
    );
    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sectionIds.length]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleEngine = (id) => {
    setExpandedEngines(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div data-testid="teacher-guide-page" style={{ display: 'flex', height: '100%', background: '#F8F9FB' }}>

      {/* ─── Table of Contents ─── */}
      <nav
        data-testid="guide-toc"
        style={{
          width: 210, minWidth: 210, flexShrink: 0,
          borderRight: '1px solid #F1F5F9', background: 'white',
          padding: '2rem 0', overflow: 'auto',
        }}
      >
        <div style={{
          padding: '0 1.25rem', marginBottom: '1.5rem',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <BookOpen size={16} color="#1E2140" />
          <span style={{
            fontFamily: 'Outfit, sans-serif', fontSize: '0.9rem',
            fontWeight: 700, color: '#1A1D20',
          }}>
            {t('guide.toc')}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {sectionIds.map(id => {
            const isActive = activeSection === id;
            const isWalkthrough = id === 'walkthrough';
            const label = isWalkthrough
              ? (t('guide.sec.walkthrough') || 'Quick Video Walkthrough')
              : t(SECTION_LABEL_KEYS[id]);
            return (
              <button
                key={id}
                data-testid={`toc-${id}`}
                onClick={() => scrollToSection(id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  width: '100%', textAlign: 'left',
                  padding: '0.5rem 1.25rem',
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem',
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? '#1E2140' : '#64748B',
                  background: isActive ? '#F1F5F9' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  borderRight: isActive ? '2px solid #1E2140' : '2px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                {isWalkthrough && <PlayCircle size={14} color={isActive ? '#1E2140' : '#64748B'} />}
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* ─── Content ─── */}
      <div
        ref={contentRef}
        style={{ flex: 1, overflow: 'auto', padding: '2rem 2.5rem 4rem' }}
      >
        <div style={{ maxWidth: 720 }}>

          {/* ─── Hero ─── */}
          <div style={{ marginBottom: '1rem' }}>
            <h1
              data-testid="guide-title"
              style={{
                fontFamily: 'Outfit, sans-serif', fontSize: '1.75rem', fontWeight: 700,
                color: '#1A1D20', margin: '0 0 0.5rem',
              }}
            >
              {t('guide.title')}
            </h1>
            <p style={{
              fontFamily: 'Nunito, sans-serif', fontSize: '1rem',
              color: '#64748B', margin: 0, lineHeight: 1.6,
            }}>
              {t('guide.subtitle')}
            </p>
          </div>
          <P>{t('guide.intro')}</P>

          {/* ─── Quick Video Walkthrough (owner-pushed Vimeo embed) - sits at top, above Getting Started ─── */}
          {walkthrough?.embed_url && (
            <div id="walkthrough" style={{ scrollMarginTop: '1rem' }}>
              <SectionHeading id="walkthrough-heading">
                {t('guide.sec.walkthrough') || 'Quick Video Walkthrough'}
              </SectionHeading>
              <P>
                {t('guide.walk.intro') || 'A short, narrated tour of the platform. Watch it before your first session to see how the pieces fit together.'}
              </P>
              <div
                data-testid="walkthrough-video-frame"
                style={{
                  margin: '1.5rem 0 2.5rem',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
                  border: '1px solid #E2E8F0',
                  background: '#000',
                  aspectRatio: '16 / 9',
                }}
              >
                <iframe
                  src={walkthrough.embed_url}
                  title="Quick Video Walkthrough"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  style={{ display: 'block', border: 0 }}
                />
              </div>
            </div>
          )}

          {/* ═══ SECTION 1: GETTING STARTED ═══ */}
          <SectionHeading id="getting-started">{t('guide.sec.gettingStarted')}</SectionHeading>
          <P>{t('guide.gs.p1')}</P>
          <P>{t('guide.gs.p2')}</P>
          <ScreenFrame src="/guide/dashboard.jpg" alt="dashboard" caption={t('guide.gs.caption')} />
          <P>{t('guide.gs.p3')}</P>

          {/* ═══ SECTION 2: TWO WAYS TO USE THE PLATFORM ═══ */}
          <SectionHeading id="two-engines">{t('guide.sec.twoEngines')}</SectionHeading>
          <P>{t('guide.te.intro')}</P>
          <SubHeading>{t('guide.te.browseTitle')}</SubHeading>
          <P>{t('guide.te.browseDesc')}</P>
          <ScreenFrame src="/guide/two_engines_browse.jpg" alt="two-engines-browse" caption={t('guide.te.browseCaption')} />
          <SubHeading>{t('guide.te.programsTitle')}</SubHeading>
          <P>{t('guide.te.programsDesc')}</P>
          <ScreenFrame src="/guide/two_engines_programs.jpg" alt="two-engines-programs" caption={t('guide.te.programsCaption')} />
          <P>{t('guide.te.together')}</P>
          <P>{t('guide.te.bridge')}</P>

          {/* ═══ SECTION 3: FINDING CONTENT ═══ */}
          <SectionHeading id="finding-content">{t('guide.sec.findingContent')}</SectionHeading>
          <P>{t('guide.fc.p1')}</P>
          <P>{t('guide.fc.p2')}</P>
          <SubHeading>{t('guide.fc.schoolYear')}</SubHeading>
          <P>{t('guide.fc.schoolDesc')}</P>
          <ScreenFrame src="/guide/browse_school.jpg" alt="browse-school" caption={t('guide.fc.schoolCaption')} />
          <SubHeading>{t('guide.fc.byLevel')}</SubHeading>
          <P>{t('guide.fc.levelDesc')}</P>
          <ScreenFrame src="/guide/browse_level.jpg" alt="browse-level" caption={t('guide.fc.levelCaption')} />
          <SubHeading>{t('guide.fc.byTopic')}</SubHeading>
          <P>{t('guide.fc.topicDesc')}</P>
          <ScreenFrame src="/guide/browse_topic.jpg" alt="browse-topic" caption={t('guide.fc.topicCaption')} />
          <SubHeading>{t('guide.fc.sameContent')}</SubHeading>
          <P>{t('guide.fc.sameP1')}</P>
          <P>{t('guide.fc.sameP2')}</P>
          <Tip>{t('guide.fc.sameTip')}</Tip>

          {/* ═══ SECTION 3: RUNNING AN ACTIVITY ═══ */}
          <SectionHeading id="running-activity">{t('guide.sec.runningActivity')}</SectionHeading>
          <P>{t('guide.ra.p1')}</P>
          <P>{t('guide.ra.p2')}</P>
          <SubHeading>{t('guide.ra.difficulty')}</SubHeading>
          <P>{t('guide.ra.diffDesc')}</P>
          <SubHeading>{t('guide.ra.gameSpecific')}</SubHeading>
          <P>{t('guide.ra.gameSpecificDesc')}</P>
          <ScreenFrame src="/guide/game_setup.jpg" alt="game-setup" caption={t('guide.ra.setupCaption')} />
          <SubHeading>{t('guide.ra.preview')}</SubHeading>
          <P>{t('guide.ra.previewDesc')}</P>
          <SubHeading>{t('guide.ra.participation')}</SubHeading>
          <P>{t('guide.ra.participationDesc')}</P>
          <P>{t('guide.ra.launchDesc')}</P>

          {/* ═══ SECTION 4: GAME ENGINES ═══ */}
          <SectionHeading id="game-engines">{t('guide.sec.gameEngines')}</SectionHeading>
          <P>{t('guide.ge.p1')}</P>
          <P>{t('guide.ge.p2')}</P>
          <P>{t('guide.ge.p3')}</P>
          <P>{t('guide.ge.p4')}</P>
          <SubHeading>{t('guide.ge.howItWorks')}</SubHeading>
          <P>{t('guide.ge.howP1')}</P>
          <P>{t('guide.ge.howP2')}</P>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1.25rem' }}>
            {ENGINES.map(engine => (
              <EngineCard
                key={engine.id}
                engine={engine}
                expanded={expandedEngines.has(engine.id)}
                onToggle={() => toggleEngine(engine.id)}
                t={t}
              />
            ))}
          </div>

          {/* ═══ SECTION 5: SAVING YOUR SETUP ═══ */}
          <SectionHeading id="saving-setup">{t('guide.sec.savingSetup')}</SectionHeading>
          <P>{t('guide.ss.p1')}</P>
          <P>{t('guide.ss.p2')}</P>
          <Tip>{t('guide.ss.tip')}</Tip>

          {/* ═══ SECTION 6: TEACHING SEQUENCES ═══ */}
          <SectionHeading id="teaching-sequences">{t('guide.sec.teachingSequences')}</SectionHeading>
          <P>{t('guide.ts.p1')}</P>
          <P>{t('guide.ts.p2')}</P>
          <P>{t('guide.ts.p3')}</P>
          <SubHeading>{t('guide.ts.samplePlaylists')}</SubHeading>
          <P>{t('guide.ts.sampleP1')}</P>
          <ul style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#374151',
            lineHeight: 1.75, margin: '0 0 1rem', paddingLeft: '1.25rem',
          }}>
            {(t('guide.ts.sampleList') || []).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <P>{t('guide.ts.sampleP2')}</P>
          <Tip>{t('guide.ts.sampleTip')}</Tip>

          {/* ═══ SECTION 7: TRACKING PROGRESS ═══ */}
          <SectionHeading id="tracking-progress">{t('guide.sec.trackingProgress')}</SectionHeading>
          <P>{t('guide.tp.p1')}</P>
          <SubHeading>{t('guide.tp.dashOverview')}</SubHeading>
          <P>{t('guide.tp.dashP1')}</P>
          <SubHeading>{t('guide.tp.progressPage')}</SubHeading>
          <P>{t('guide.tp.progressP1')}</P>
          <P><Strong>{t('guide.tp.cefrView')}</Strong></P>
          <P><Strong>{t('guide.tp.currView')}</Strong></P>
          <ScreenFrame src="/guide/progress.jpg" alt="progress" caption={t('guide.tp.progressCaption')} />
          <SubHeading>{t('guide.tp.pdfReports')}</SubHeading>
          <P>{t('guide.tp.pdfP1')}</P>
          <ul style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#374151',
            lineHeight: 1.75, margin: '0 0 1rem', paddingLeft: '1.25rem',
          }}>
            {(t('guide.tp.pdfList') || []).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <P>{t('guide.tp.pdfP2')}</P>
          <Tip>{t('guide.tp.pdfTip')}</Tip>

          {/* ═══ SECTION 8: GROUPS & CLASS PREP ═══ */}
          <SectionHeading id="groups">{t('guide.sec.groups')}</SectionHeading>
          <P>{t('guide.gr.p1')}</P>
          <SubHeading>{t('guide.gr.groups')}</SubHeading>
          <P>{t('guide.gr.groupsP1')}</P>
          <SubHeading>{t('guide.gr.classPrep')}</SubHeading>
          <P>{t('guide.gr.classPrepP1')}</P>
          <P>{t('guide.gr.classPrepP2')}</P>
          <ScreenFrame src="/guide/classroom_switcher.jpg" alt="classroom-switcher" caption={t('guide.gr.switcherCaption')} />
          <SubHeading>{t('guide.gr.switching')}</SubHeading>
          <P>{t('guide.gr.switchingP1')}</P>
          <Tip>{t('guide.gr.switchingTip')}</Tip>

          {/* ─── Footer ─── */}
          <div style={{
            marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #E2E8F0',
          }}>
            <p style={{
              fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#94A3B8',
              lineHeight: 1.6, margin: 0,
            }}>
              {t('guide.footer')} <a href="mailto:base@whitelane.com" style={{ color: '#1E2140', textDecoration: 'none', fontWeight: 600 }}>base@whitelane.com</a>.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
